import {useCallback, useRef} from 'react'
import {useCLIPromptState} from './use-cli-prompt-state'
import {useCreateCommand} from './useCreateCommand'
import {useOptimisticLogs} from '../contexts/OptimisticLogsContext'
import {COMMAND_TYPES} from '../constants/command-types'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import {findSessionFanoutTargets} from '../utils/permission-prompt-helpers'
import {SessionState, type CommandType} from '../types/session'

export interface UsePromptResponseSubmissionOptions {
  /** The session whose prompt should be monitored and responded to. */
  sessionId: string | undefined
  /** Whether to enable the underlying CLI prompt state hook (typically: !!sessionId
   *  AND the session is in a state that can accept user input). */
  enabled: boolean
  /** Current session state. */
  sessionState?: SessionState
  /** Optional callback invoked when the prompt-response createCommand mutation
   *  fails, after the optimistic record has been rewound. Use this to surface
   *  a user-visible error in the consumer (e.g. setFailedToSubmit). */
  onError?: () => void
}

export interface PromptResponseSubmission {
  /** The current pending prompt, or null when none. */
  pendingPrompt: NormalizedPrompt | null
  /** True while the underlying logs query is loading. */
  isLoading: boolean
  /**
   * True when the pending prompt is older than the resume window AND the
   * session is paused (`waiting_for_user`, i.e. CCA only).
   */
  isExpired: boolean
  /** True while a prompt-response createCommand mutation is in flight. */
  isPending: boolean
  /** Dismiss the current prompt without sending a response. */
  dismissPrompt: () => void
  /** Submit a response for the pending prompt. No-op when there is no pending
   *  prompt, a previous response is in flight, or sessionId is missing. */
  handlePromptRespond: (toolCallId: string, response: unknown, commandType: CommandType) => void
}

/**
 * Wires together {@link useCLIPromptState}, {@link useOptimisticLogs}, and a
 * dedicated {@link useCreateCommand} mutation for components that render an
 * interactive prompt (permission_request, ask_user, exit_plan_mode) and submit
 * the user's response.
 *
 * Resolution is optimistic: the prompt is marked resolved synchronously on
 * click, and the rollback in `onError` re-shows it if the underlying
 * createCommand mutation fails.
 */
export function usePromptResponseSubmission({
  sessionId,
  enabled,
  sessionState,
  onError,
}: UsePromptResponseSubmissionOptions): PromptResponseSubmission {
  const {pendingPrompt, isLoading, isExpired, logs, markPromptResolved, unmarkPromptResolved, dismissPrompt} =
    useCLIPromptState({
      sessionId,
      enabled,
      sessionState,
    })
  const {addOptimisticResponse, removeOptimisticResponse} = useOptimisticLogs()
  const optimisticIdRef = useRef<string | null>(null)
  const optimisticResolvedToolCallIdRef = useRef<string | null>(null)
  const sessionFanoutResolvedToolCallIdsRef = useRef<string[]>([])
  const sessionFanoutOptimisticIdsRef = useRef<string[]>([])

  const {createCommand, isPending} = useCreateCommand({
    onSuccess: () => {
      optimisticIdRef.current = null
      optimisticResolvedToolCallIdRef.current = null
      sessionFanoutResolvedToolCallIdsRef.current = []
      sessionFanoutOptimisticIdsRef.current = []
    },
    onError: () => {
      if (optimisticIdRef.current) {
        removeOptimisticResponse(optimisticIdRef.current)
        optimisticIdRef.current = null
      }
      if (optimisticResolvedToolCallIdRef.current) {
        unmarkPromptResolved(optimisticResolvedToolCallIdRef.current)
        optimisticResolvedToolCallIdRef.current = null
      }
      for (const id of sessionFanoutOptimisticIdsRef.current) {
        removeOptimisticResponse(id)
      }
      for (const tcid of sessionFanoutResolvedToolCallIdsRef.current) {
        unmarkPromptResolved(tcid)
      }
      sessionFanoutResolvedToolCallIdsRef.current = []
      sessionFanoutOptimisticIdsRef.current = []
      onError?.()
    },
  })

  const handlePromptRespond = useCallback(
    (toolCallId: string, response: unknown, commandType: CommandType) => {
      if (!pendingPrompt || isPending || !sessionId) return

      // Only surface the "Resuming session…" indicator when the click is
      // recovering a paused (waiting_for_user) session. CLI sessions don't
      // pause, so showing the indicator there is misleading.
      const fromPausedSession = sessionState === SessionState.WaitingForUser

      optimisticIdRef.current = addOptimisticResponse({
        toolCallId,
        response,
        commandType,
        fromPausedSession,
      })
      markPromptResolved(toolCallId)
      optimisticResolvedToolCallIdRef.current = toolCallId

      sessionFanoutResolvedToolCallIdsRef.current = []
      sessionFanoutOptimisticIdsRef.current = []
      const sessionFanoutTargets =
        commandType === COMMAND_TYPES.PERMISSION_RESPONSE
          ? findSessionFanoutTargets(pendingPrompt, response, logs, toolCallId)
          : []
      for (const target of sessionFanoutTargets) {
        markPromptResolved(target.toolCallId)
        sessionFanoutResolvedToolCallIdsRef.current.push(target.toolCallId)
        const optId = addOptimisticResponse({
          toolCallId: target.toolCallId,
          response: {...(response as object), promptId: target.toolCallId},
          commandType,
          inferred: true,
          fromPausedSession,
        })
        sessionFanoutOptimisticIdsRef.current.push(optId)
      }

      // Forward the prompt-specific command type (e.g. PERMISSION_RESPONSE,
      // ASK_USER_RESPONSE) so Mission Control can route the command correctly:
      // CCAv3 steering filters on `command.type !== 'user_message'` to avoid
      // injecting prompt responses as raw user steering messages.
      createCommand({
        sessionId,
        content: JSON.stringify(response),
        type: commandType,
      })
    },
    [pendingPrompt, isPending, sessionId, sessionState, createCommand, addOptimisticResponse, markPromptResolved, logs],
  )

  return {pendingPrompt, isLoading, isExpired, isPending, dismissPrompt, handlePromptRespond}
}
